/**
 * HelpGuidesPage
 * CCH Care — Phase 5f
 *
 * In-app quick reference guides (accordion) + external links.
 * No backend required — static content.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../config/constants';

const SUPPORT_EMAIL = 'healthmanager@clientcarehealth.org';

// ── Guide content ─────────────────────────────────────────────

const GUIDES = [
  {
    key:   'cna_quickstart',
    icon:  '🧑‍⚕️',
    title: 'CNA Quick Start',
    color: COLORS.accent,
    steps: [
      { heading: 'Start your shift', body: 'Log in and go to the Dashboard. Your active shift and assigned residents will appear automatically. If your shift does not show, contact your supervisor.' },
      { heading: 'Open a resident\'s ADL', body: 'Tap a resident\'s name from the shift list. Each ADL section (Hygiene, Meals, Repositioning, etc.) is a collapsible card. Tap the card header to expand it.' },
      { heading: 'Document each section', body: 'Fill in the relevant fields and tap Save before moving to the next section. You can return and edit unsigned sections at any time during your shift.' },
      { heading: 'Sign a section', body: 'When documentation is complete, tap Sign on the section card. Signed sections are locked — use the amendment flow if a correction is needed.' },
      { heading: 'Vitals and MAR', body: 'Vitals are added as timed readings — tap + Add Reading. For medications, navigate to More → Medications (MAR). Document each dose as Given, Refused, or Held.' },
      { heading: 'Bedtime checklist', body: 'At the end of an evening shift, navigate to More → Bedtime Checklist and complete the checklist for each resident before signing off.' },
    ],
  },
  {
    key:   'supervisor_guide',
    icon:  '🔍',
    title: 'Supervisor & Admin Guide',
    color: COLORS.primary,
    steps: [
      { heading: 'Reviewing ADL records', body: 'Open any resident\'s ADL flow sheet. When all 8 CNA sections are signed, a Supervisor Review button appears. Tap it to co-sign the record and mark it supervisor-reviewed.' },
      { heading: 'Shift History', body: 'Navigate to More → Shift History to review past shifts. Filter by date range or unit. Tap a shift to see each resident\'s ADL completion, and tap a resident to view the full read-only ADL summary.' },
      { heading: 'Reports', body: 'The Reports tab provides ADL compliance, MAR compliance, vitals trends, staff activity, and controlled substance summaries across any date range.' },
      { heading: 'Managing staff', body: 'Go to More → Staff Management to invite CNAs, assign roles, and grant or revoke Care module access. New staff receive an email invitation.' },
      { heading: 'Organization settings', body: 'Go to More → Organization Settings to update the facility name, address, timezone, and billing contact information.' },
      { heading: 'Alerts and notifications', body: 'The bell icon in the header shows care alerts. Critical alerts (abnormal vitals, fall risk, missed repositioning) appear in real time. Tap an alert to acknowledge it.' },
    ],
  },
  {
    key:   'adl_guide',
    icon:  '📋',
    title: 'ADL Sections Reference',
    color: '#8E44AD',
    steps: [
      { heading: 'Hygiene', body: 'Document bathing, oral care, grooming, dressing, and skin care activities. Select the type and level of assistance provided.' },
      { heading: 'Meals', body: 'Record intake percentage for each meal (Breakfast, Lunch, Dinner). Mark as Refused if the resident declined. Add notes for any concerns.' },
      { heading: 'Repositioning', body: 'Add a timed entry each time the resident is repositioned. Record the time, position, and any skin concerns observed.' },
      { heading: 'Fluid Intake', body: 'Log each fluid intake event with the amount in ml and the type of fluid. The running daily total updates automatically.' },
      { heading: 'Bowel & Urination', body: 'Record elimination events with consistency, amount, and any abnormalities. Each event is time-stamped.' },
      { heading: 'Skin Checks', body: 'Document each body area assessed. Mark as Intact or flag concerns. Areas with concerns automatically generate a care alert.' },
      { heading: 'Safety', body: 'Check off each applicable safety item (call light, bed alarm, non-slip socks, etc.). Items on the checklist are configured by your administrator.' },
    ],
  },
  {
    key:   'mar_guide',
    icon:  '💊',
    title: 'MAR & Medications',
    color: '#E67E22',
    steps: [
      { heading: 'Viewing medications', body: 'Navigate to More → Medications (MAR). Select a resident to see their active medication list with scheduled times and current shift status.' },
      { heading: 'Documenting a scheduled dose', body: 'Tap a medication row, select Given, Refused, or Held, enter the administration time, and save. Each dose generates a MAR record with your name and timestamp.' },
      { heading: 'PRN medications', body: 'For as-needed medications, tap + PRN Dose. You must enter an indication (reason) and a pain score (0–10) before saving.' },
      { heading: 'Controlled substances', body: 'Navigate to More → Controlled Substances for shift-change counts, waste documentation, and discrepancy management. A witness membership ID is required for all entries.' },
      { heading: 'Discrepancies', body: 'When a count does not match the expected value, a discrepancy report is generated automatically. Supervisors can update the status and add resolution notes from the Discrepancies tab.' },
    ],
  },
  {
    key:   'troubleshooting',
    icon:  '🛠️',
    title: 'Troubleshooting',
    color: COLORS.danger,
    steps: [
      { heading: 'Session expired — please log in again', body: 'Sessions expire after a period of inactivity for security. Log in again and your data will be intact. If this happens frequently, check that your device clock is correct.' },
      { heading: 'My shift is not showing', body: 'Shifts are configured by your administrator. Confirm your shift date and unit with your supervisor. If the shift exists but does not appear, try refreshing the page.' },
      { heading: 'I cannot sign a section', body: 'All required fields in the section must be completed before signing. Look for any highlighted or missing fields. If the section was already signed by another CNA, contact your supervisor.' },
      { heading: 'A resident is missing from my list', body: 'Residents are assigned to units by your administrator. If a resident is not appearing, confirm their unit assignment in Facility Setup.' },
      { heading: 'I made an error after signing', body: 'Signed sections cannot be edited directly. Open the signed section and use the Add Amendment option to document a correction with an explanation.' },
      { heading: 'The app is slow or not loading', body: 'Check your internet connection. If the issue persists, try a hard refresh (Ctrl+Shift+R on desktop, or close and reopen on mobile). Contact support if the problem continues.' },
    ],
  },
];

const EXTERNAL_LINKS = [
  {
    icon:  '📖',
    label: 'Full User Manual',
    sub:   'Complete documentation for all modules',
    url:   'https://care.clientcarehealth.org/docs/index.html',
  },
  {
    icon:  '🔒',
    label: 'HIPAA & Privacy Policy',
    sub:   'Data handling and compliance information',
    url:   'https://care.clientcarehealth.org/docs/hipaa.html',
  },
  {
    icon:  '🎬',
    label: 'Video Walkthroughs',
    sub:   'Step-by-step setup and documentation guides',
    url:   'https://care.clientcarehealth.org/docs/videos.html',
  },
  {
    icon:  '📣',
    label: 'Release Notes',
    sub:   'What\'s new in CCH Care',
    url:   'https://care.clientcarehealth.org/docs/changelog.html',
  },
];

// ── Component ─────────────────────────────────────────────────

export default function HelpGuidesPage() {
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState(null);
  const [search, setSearch]   = useState('');

  function toggle(key) {
    setOpenKey(prev => prev === key ? null : key);
  }

  const query = search.trim().toLowerCase();
  const visibleGuides = query
    ? GUIDES.filter(g =>
        g.title.toLowerCase().includes(query) ||
        g.steps.some(s => s.heading.toLowerCase().includes(query) || s.body.toLowerCase().includes(query))
      )
    : GUIDES;

  return (
    <div style={{ paddingBottom: 80 }}>

      {/* Header */}
      <div style={{
        padding: '12px 16px', background: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <button
            onClick={() => navigate('/dashboard/more')}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: COLORS.primary, padding: '2px 6px' }}
          >
            ←
          </button>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>Help &amp; Guides</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>Quick reference and support resources</div>
          </div>
        </div>

        {/* Search */}
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            fontSize: 14, color: COLORS.textMuted, pointerEvents: 'none',
          }}>
            🔍
          </span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search guides…"
            style={{
              width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8,
              border: `1px solid ${COLORS.border}`, fontSize: 13,
              color: COLORS.text, background: COLORS.bg,
              fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      <div style={{ padding: 16 }}>

        {/* Quick contact */}
        <div style={{
          background: COLORS.primaryLight, border: `1px solid ${COLORS.primaryAlpha}`,
          borderRadius: 10, padding: '12px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 24, flexShrink: 0 }}>💬</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>Need help from our team?</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>We typically respond within 1 business day.</div>
          </div>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              background: COLORS.primary, color: '#fff', textDecoration: 'none', flexShrink: 0,
            }}
          >
            Email Us
          </a>
        </div>

        {/* Guides */}
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          Quick Reference Guides
        </div>

        {visibleGuides.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: COLORS.textMuted, fontSize: 13 }}>
            No guides match &ldquo;{search}&rdquo;
          </div>
        )}

        {visibleGuides.map(guide => {
          const isOpen = openKey === guide.key;
          const filteredSteps = query
            ? guide.steps.filter(s => s.heading.toLowerCase().includes(query) || s.body.toLowerCase().includes(query))
            : guide.steps;

          return (
            <div key={guide.key} style={{
              background: COLORS.surface, borderRadius: 10,
              border: `1px solid ${isOpen ? guide.color + '44' : COLORS.border}`,
              marginBottom: 8, overflow: 'hidden',
            }}>
              {/* Accordion header */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggle(guide.key)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggle(guide.key); }}
                style={{
                  padding: '12px 14px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 12,
                  background: isOpen ? `${guide.color}08` : 'transparent',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                  background: `${guide.color}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18,
                }}>
                  {guide.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{guide.title}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>{guide.steps.length} topics</div>
                </div>
                <span style={{
                  fontSize: 14, color: COLORS.textMuted, flexShrink: 0,
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}>▼</span>
              </div>

              {/* Accordion body */}
              {isOpen && (
                <div style={{ borderTop: `1px solid ${COLORS.border}` }}>
                  {filteredSteps.map((step, i) => (
                    <div key={i} style={{
                      padding: '12px 16px',
                      borderBottom: i < filteredSteps.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                    }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: guide.color, marginBottom: 4,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <span style={{
                          fontSize: 10, fontWeight: 800, background: guide.color,
                          color: '#fff', width: 18, height: 18, borderRadius: '50%',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          {i + 1}
                        </span>
                        {step.heading}
                      </div>
                      <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.55, paddingLeft: 24 }}>
                        {step.body}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* External links */}
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, marginTop: 24 }}>
          Documentation &amp; Resources
        </div>

        {EXTERNAL_LINKS.map((link, i) => (
          <a
            key={i}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: COLORS.surface, borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              padding: '12px 14px', marginBottom: 8,
              textDecoration: 'none',
            }}
          >
            <span style={{ fontSize: 22, flexShrink: 0 }}>{link.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{link.label}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>{link.sub}</div>
            </div>
            <span style={{ fontSize: 12, color: COLORS.primary, fontWeight: 600, flexShrink: 0 }}>↗</span>
          </a>
        ))}

        {/* Version footer */}
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: COLORS.textMuted }}>
          CCH Care · Phase 5f
        </div>
      </div>
    </div>
  );
}
